import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ImageData, ImagesService, Resolution } from '@libraries/assets';
import { Language } from '@libraries/design-system';
import { isMobile } from '@libraries/ui-state';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

type FooterArrow = false | 'left' | 'right';

@Component({
  selector: 'generic-success-page',
  templateUrl: './generic-success-page.component.html',
  styleUrls: ['./generic-success-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GenericSuccessPageComponent implements OnChanges {
  @Input() icon: IconProp;
  @Input() title: string;
  @Input() bigText: boolean;
  @Input() primaryButton: string;
  @Input() secondaryButton: string;
  @Input() image: string;
  @Input() showLeftArrow: FooterArrow = 'left';
  @Input() showRightArrow: FooterArrow = 'right';
  @Input() hidePortalMenu = false;
  @Input() currentLanguage: Language = Language.English;
  @Input() showLanguageToggler: boolean;
  @Output() languageChange = new EventEmitter<Language>();
  @Input() showImage = true;
  @Input() isHoADocs = false;

  imageData$: Observable<ImageData>;
  imageChanges = new BehaviorSubject('');

  resolution: Resolution;
  resolutionChanges = this.store.select(isMobile).pipe(tap(this.updateResolution.bind(this)));

  @Output() primaryAction = new EventEmitter();
  @Output() secondaryAction = new EventEmitter();

  get isMobile() {
    return this.resolution === Resolution.mobile;
  }

  constructor(private store: Store, public images: ImagesService) {
    this.imageData$ = combineLatest([this.imageChanges, this.resolutionChanges]).pipe(
      filter(() => Boolean(this.image && this.resolution)),
      switchMap(() => this.images.get$(this.image + this.resolution))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.image && changes.image.previousValue !== changes.image.currentValue) {
      this.imageChanges.next(changes.image.currentValue);
    }
  }

  updateResolution(mobile: boolean) {
    this.resolution = mobile ? Resolution.mobile : Resolution.desktop;
  }

  primaryClicked() {
    this.primaryAction.emit();
  }

  secondaryClicked() {
    this.secondaryAction.emit();
  }

  toggleLanguage(language) {
    this.languageChange.emit(language);
  }
}
